import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Award, CheckCircle2, FileWarning } from 'lucide-react';
import api from '../services/api';

interface VerifiedCertificate {
  valid: boolean;
  certificateType: 'EXAM' | 'SCHOLARSHIP';
  certificateId: string;
  studentName?: string;
  title?: string;
  subject?: string;
  className?: string;
  score?: number;
  maximumScore?: number;
  percentageScore?: number;
  grade?: string;
  rank?: number;
  prizeTitle?: string;
  prizeAmount?: number;
  issuedAt?: string;
  issuedByName?: string;
}

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatCurrency = (value?: number) =>
  value
    ? new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(value)
    : '';

const CertificateVerificationPage: React.FC = () => {
  const { certificateId = '' } = useParams();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState<VerifiedCertificate | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const verify = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get<VerifiedCertificate>(
          `/certificates/verify/${encodeURIComponent(certificateId)}`,
        );
        if (!mounted) return;
        setCertificate(response.data);
      } catch (err) {
        console.error('Certificate verification failed:', err);
        if (!mounted) return;
        setCertificate(null);
        setError('Certificate could not be verified.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void verify();
    return () => {
      mounted = false;
    };
  }, [certificateId]);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#fff7ed_52%,_#eef2ff_100%)] px-4 py-14 text-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-lg border border-white/70 bg-white/95 p-6 shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:p-8">
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-indigo-50 p-3 text-indigo-700">
              <Award className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-700">
                Certificate Verification
              </p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
                LocalAlly certificate
              </h1>
              <p className="mt-2 text-sm text-slate-600">{certificateId}</p>
            </div>
          </div>

          {loading ? (
            <div className="mt-8 rounded-lg border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-600">
              Verifying certificate...
            </div>
          ) : error || !certificate?.valid ? (
            <div className="mt-8 rounded-lg border border-rose-200 bg-rose-50 p-5 text-rose-800">
              <div className="flex items-center gap-3">
                <FileWarning className="h-5 w-5" />
                <p className="font-semibold">Verification unavailable</p>
              </div>
              <p className="mt-2 text-sm">{error || 'Certificate could not be verified.'}</p>
            </div>
          ) : (
            <div className="mt-8 space-y-5">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-emerald-900">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5" />
                  <p className="font-semibold">Verified certificate</p>
                </div>
                <p className="mt-2 text-sm">
                  This certificate number matches a LocalAlly issued record.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Recipient</p>
                  <p className="mt-2 text-lg font-semibold text-slate-950">{certificate.studentName || '-'}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Issued</p>
                  <p className="mt-2 text-lg font-semibold text-slate-950">{formatDate(certificate.issuedAt)}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 sm:col-span-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Certificate</p>
                  <p className="mt-2 text-lg font-semibold text-slate-950">{certificate.title || '-'}</p>
                  <p className="mt-1 text-sm text-slate-600">
                    {[certificate.className, certificate.subject].filter(Boolean).join(' | ')}
                  </p>
                </div>
                {certificate.percentageScore !== undefined && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Score</p>
                    <p className="mt-2 text-lg font-semibold text-slate-950">
                      {certificate.maximumScore
                        ? `${certificate.score || 0} / ${certificate.maximumScore}`
                        : `${certificate.percentageScore}%`}
                    </p>
                  </div>
                )}
                {certificate.rank !== undefined && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Rank</p>
                    <p className="mt-2 text-lg font-semibold text-slate-950">{certificate.rank}</p>
                  </div>
                )}
                {certificate.prizeTitle && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 sm:col-span-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Recognition</p>
                    <p className="mt-2 text-lg font-semibold text-slate-950">
                      {certificate.prizeTitle}
                      {certificate.prizeAmount ? ` | ${formatCurrency(certificate.prizeAmount)}` : ''}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-8">
            <Link
              to="/"
              className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Go to LocalAlly
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateVerificationPage;
